import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useFetch } from '@/lib/fin/use-fetch';
import { useCurrency } from '@/context/CurrencyContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
} from 'recharts';
import {
  Users, ChevronDown, ChevronRight, UserPlus, Building2, Briefcase,
  Network, Plus, Pencil, Trash2, UserCheck, FileText, ExternalLink, Upload,
} from 'lucide-react';

/* ---------- types ---------- */

interface Contract {
  contract_id: string;
  staff_id: string | null;
  name: string | null;
  division: string | null;
  team: string | null;
  position: string | null;
  legal_entity: string | null;
  opex_capex: string | null;
  agreement_type: string | null;
  currency: string | null;
  start_date: string | null;
  end_date: string | null;
  bank_net: number | null;
  tax: number | null;
  crypto_cash: number | null;
  total_net: number | null;
  total_gross: number | null;
  is_active: boolean;
  document_url: string | null;
}

interface ContractFormData {
  contract_id: string;
  staff_id: string;
  name: string;
  legal_entity: string;
  position: string;
  division: string;
  team: string;
  agreement_type: string;
  opex_capex: string;
  currency: string;
  bank_net: string;
  tax: string;
  crypto_cash: string;
  total_net: string;
  total_gross: string;
  start_date: string;
  end_date: string;
  document_url: string;
}

const emptyContractForm: ContractFormData = {
  contract_id: '', staff_id: '', name: '', legal_entity: '__none__',
  position: '', division: '__none__', team: '', agreement_type: '__none__',
  opex_capex: '__none__', currency: 'EUR',
  bank_net: '', tax: '', crypto_cash: '', total_net: '', total_gross: '',
  start_date: '', end_date: '', document_url: '',
};

const AGREEMENT_TYPES = ['Employment Agreement', 'Service Agreement', 'Contractor', 'Consultant'];
const CURRENCIES = ['EUR', 'USD', 'GBP', 'CHF', 'AED'];
const OPEX_CAPEX = ['OPEX', 'CAPEX'];

interface Position {
  id: string;
  group_name: string;
  division: string | null;
  department: string | null;
  team: string | null;
  position_name: string;
  status: string;
  allocation_pct: number | null;
  salary_gross: number | null;
  opex_capex: string | null;
}

interface Employee {
  staff_id: string;
  name: string;
  num_contracts: number | null;
  active_contracts: number | null;
  geography: string | null;
  division: string | null;
  position: string | null;
  grade: string | null;
  total_salary_net: number | null;
  total_salary_gross: number | null;
  /** Latest-month total employer cost from Cyprus payroll DB:
   *  gross + employer SIC + NHS + COH + RED + IND. Null for staff
   *  not in the payroll DB extract (UAE, USA, etc.). */
  employer_cost_eur: number | null;
  bonus_cap: number | null;
  total_allocation: number | null;
  alloc_wwg: number | null;
  alloc_tria: number | null;
  alloc_tff: number | null;
  is_active: boolean;
  contracts: Contract[];
  positions: Position[];
}

/** Latest-month snapshot from public/data/payroll_actual.json (Cyprus payroll DB).
 *  Indexed by normalized employee name so the expanded employee row can show
 *  the breakdown without needing a database round-trip. */
interface PayrollSnapshot {
  company_name: string;
  company_code: string;
  employee_code: string;
  year_month: string;
  gross_eur: number;
  basic_eur: number;
  bonus_eur: number;
  overtime_eur: number;
  allowances_eur: number;
  extras_eur: number;
  insurable_basis_eur: number;
  employer_sic_eur: number;
  employer_nhs_eur: number;
  employer_coh_eur: number;
  employer_red_eur: number;
  employer_ind_eur: number;
  employer_total_eur: number;
  total_cost_eur: number;
}

const normName = (s: string | null | undefined) => (s || '').toLowerCase().replace(/\s+/g, ' ').trim();

interface PositionRow {
  id: string;
  group_name: string;
  division: string | null;
  department: string | null;
  team: string | null;
  commercial_role: string | null;
  status: string;
  opex_capex: string | null;
  position_name: string;
  staff_id: string | null;
  vacancy_id: string | null;
  employee_name: string | null;
  allocation_pct: number | null;
  salary_gross: number | null;
  employee_full_name: string | null;
  employee_geography: string | null;
}

interface HiringEntry {
  id: string;
  group_name: string | null;
  division: string | null;
  department: string | null;
  team: string | null;
  vacancy_name: string | null;
  hiring_manager: string | null;
  legal_entity: string | null;
  geography: string | null;
  recruitment_type: string | null;
  salary_cap: number | null;
  salary_currency: string | null;
  hire_date_est: string | null;
  is_active: boolean;
  note: string | null;
  staff_id: string | null;
}

interface OrgData {
  hierarchy: Record<string, {
    total: number; filled: number; vacant: number;
    divisions: Record<string, {
      total: number; filled: number; vacant: number;
      departments: Record<string, {
        total: number; filled: number; vacant: number;
        teams: Record<string, { total: number; filled: number; vacant: number }>;
      }>;
    }>;
  }>;
  lookups: {
    groups: string[];
    divisions: string[];
    departments: string[];
    teams: string[];
  };
}

const DONUT_COLORS = ['#0C395A', '#7C3AED', '#10B981', '#F59E0B', '#EF4444', '#06B6D4', '#EC4899', '#84CC16'];

const STATUS_BADGE: Record<string, { variant: 'default' | 'secondary' | 'outline' | 'destructive'; className: string }> = {
  Filled: { variant: 'default', className: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-emerald-200' },
  Vacant: { variant: 'default', className: 'bg-amber-100 text-amber-800 hover:bg-amber-100 border-amber-200' },
  Closed: { variant: 'default', className: 'bg-gray-100 text-gray-600 hover:bg-gray-100 border-gray-200' },
  Planned: { variant: 'default', className: 'bg-blue-100 text-blue-800 hover:bg-blue-100 border-blue-200' },
};

const GEOGRAPHIES = ['Cyprus', 'UAE', 'Switzerland', 'USA', 'Cayman', 'Serbia', 'Remote'];
const DIVISIONS = ['Technology', 'Finance', 'Legal', 'Operations', 'Business Development', 'Compliance', 'Management'];
const ENTITIES = ['WWF', 'WWPI', 'WWC-CS', 'WWM', 'WWL', 'WW Capital MENA', 'Tria Software', 'Tria FinTech', 'Tria Bridge', 'SwissZeit Capital AG', 'WWF USA', 'WW Pay'];
const GROUPS = ['WWG Group', 'The Vault Group'];

// fmt provided by useCurrency — use usePayrollFmt inside components
function usePayrollFmt() {
  const { fmt: cfmt } = useCurrency();
  return (amount: number | null, currency = 'EUR') => {
    if (amount == null) return '-';
    return cfmt(amount, currency);
  };
}

function fmtDate(dateStr: string | null) {
  if (!dateStr) return '-';
  try { return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return dateStr; }
}

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_BADGE[status] || { variant: 'secondary' as const, className: '' };
  return <Badge variant={cfg.variant} className={`text-[10px] font-medium ${cfg.className}`}>{status}</Badge>;
}

function AllocationBar({ wwg, tria, tff }: { wwg: number | null; tria: number | null; tff: number | null }) {
  const w = (wwg || 0) * 100;
  const t = (tria || 0) * 100;
  const f = (tff || 0) * 100;
  const total = w + t + f;
  if (total === 0) return <span className="text-xs text-muted-foreground">-</span>;

  return (
    <div className="flex items-center gap-2">
      <div className="flex h-2 w-24 rounded-full overflow-hidden bg-gray-100">
        {w > 0 && <div className="bg-[#0C395A] h-full" style={{ width: `${w}%` }} />}
        {t > 0 && <div className="bg-[#7C3AED] h-full" style={{ width: `${t}%` }} />}
        {f > 0 && <div className="bg-[#10B981] h-full" style={{ width: `${f}%` }} />}
      </div>
      <span className="text-[10px] text-muted-foreground whitespace-nowrap">
        {w > 0 ? `W${w.toFixed(0)}` : ''}{t > 0 ? ` T${t.toFixed(0)}` : ''}{f > 0 ? ` F${f.toFixed(0)}` : ''}
      </span>
    </div>
  );
}

/* ---------- status toast ---------- */
function useStatusMessage() {
  const [status, setStatus] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showStatus = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    setStatus({ message, type });
  }, []);

  useEffect(() => {
    if (status) {
      const timer = setTimeout(() => setStatus(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  const StatusBar = status ? (
    <div className={`fixed top-4 right-4 z-[100] px-4 py-2 rounded-lg shadow-lg text-sm font-medium ${status.type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`}>
      {status.message}
    </div>
  ) : null;

  return { showStatus, StatusBar };
}

/* ==================== MAIN COMPONENT ==================== */

export default function Payroll() {
  const { data: employees, loading: empLoading, error: empError, refresh: refreshEmployees } = useFetch<Employee[]>('/api/fin/employees');
  const { data: allPositions, loading: posLoading, refresh: refreshPositions } = useFetch<PositionRow[]>('/api/fin/positions');
  const { data: hiringData, loading: hiringLoading, refresh: refreshHiring } = useFetch<HiringEntry[]>('/api/fin/hiring-plan');
  const { data: orgData, loading: orgLoading } = useFetch<OrgData>('/api/fin/org-structure');
  const { showStatus, StatusBar } = useStatusMessage();

  if (empLoading && !employees) {
    return (
      <div className="p-4 md:p-6 space-y-6">
        <Skeleton className="h-7 w-48" />
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (empError && !employees) {
    return (
      <div className="p-4 md:p-6">
        <h1 className="text-2xl font-semibold mb-2">Payroll</h1>
        <Card><CardContent className="py-12 text-center text-muted-foreground">{empError}</CardContent></Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {StatusBar}
      <div>
        <h1 className="text-2xl font-semibold text-foreground tracking-tight flex items-center gap-2">
          <Users size={24} /> HR & Payroll
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Employees, positions, org structure, and hiring plan</p>
      </div>

      <Tabs defaultValue="employees">
        <TabsList className="grid w-full grid-cols-5 max-w-3xl">
          <TabsTrigger value="employees" className="gap-1.5 text-xs">
            <Users size={14} /> Employees
          </TabsTrigger>
          <TabsTrigger value="contracts" className="gap-1.5 text-xs">
            <FileText size={14} /> Contracts
          </TabsTrigger>
          <TabsTrigger value="positions" className="gap-1.5 text-xs">
            <Briefcase size={14} /> Positions
          </TabsTrigger>
          <TabsTrigger value="hiring" className="gap-1.5 text-xs">
            <UserPlus size={14} /> Hiring Plan
          </TabsTrigger>
          <TabsTrigger value="org" className="gap-1.5 text-xs">
            <Network size={14} /> Org Structure
          </TabsTrigger>
        </TabsList>

        <TabsContent value="employees">
          <EmployeesTab employees={employees || []} onRefresh={refreshEmployees} showStatus={showStatus} />
        </TabsContent>

        <TabsContent value="contracts">
          <ContractsTab employees={employees || []} onRefresh={refreshEmployees} showStatus={showStatus} />
        </TabsContent>

        <TabsContent value="positions">
          <PositionsTab positions={allPositions || []} loading={posLoading} orgData={orgData} employees={employees || []} onRefresh={refreshPositions} showStatus={showStatus} />
        </TabsContent>

        <TabsContent value="hiring">
          <HiringTab hiringData={hiringData || []} loading={hiringLoading} onRefresh={refreshHiring} showStatus={showStatus} />
        </TabsContent>

        <TabsContent value="org">
          <OrgTab orgData={orgData} loading={orgLoading} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ==================== EMPLOYEE DIALOG ==================== */

interface EmployeeFormData {
  staff_id: string;
  name: string;
  geography: string;
  division: string;
  position: string;
  grade: string;
  alloc_wwg: string;
  alloc_tria: string;
  alloc_tff: string;
}

const emptyEmployeeForm: EmployeeFormData = {
  staff_id: '', name: '', geography: '__none__', division: '__none__', position: '', grade: '',
  alloc_wwg: '', alloc_tria: '', alloc_tff: '',
};

function EmployeeDialog({ open, onOpenChange, employee, onSave }: {
  open: boolean; onOpenChange: (v: boolean) => void;
  employee: Employee | null; onSave: (data: EmployeeFormData, isEdit: boolean) => Promise<void>;
}) {
  const isEdit = !!employee;
  const [form, setForm] = useState<EmployeeFormData>(emptyEmployeeForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      if (employee) {
        setForm({
          staff_id: employee.staff_id,
          name: employee.name,
          geography: employee.geography || '__none__',
          division: employee.division || '__none__',
          position: employee.position || '',
          grade: employee.grade || '',
          alloc_wwg: employee.alloc_wwg?.toString() || '',
          alloc_tria: employee.alloc_tria?.toString() || '',
          alloc_tff: employee.alloc_tff?.toString() || '',
        });
      } else {
        setForm({ ...emptyEmployeeForm });
      }
      setError('');
    }
  }, [open, employee]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }
    const wwg = form.alloc_wwg ? parseFloat(form.alloc_wwg) : 0;
    const tria = form.alloc_tria ? parseFloat(form.alloc_tria) : 0;
    const tff = form.alloc_tff ? parseFloat(form.alloc_tff) : 0;
    if (wwg + tria + tff > 1.01) {
      setError('Allocation splits must sum to 1.0 or less');
      return;
    }
    setSaving(true);
    try {
      await onSave(form, isEdit);
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const upd = (field: keyof EmployeeFormData, value: string) => setForm(prev => ({ ...prev, [field]: value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Employee' : 'Add Employee'}</DialogTitle>
          <DialogDescription>{isEdit ? 'Update employee details' : 'Create a new employee record'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {error && <div className="text-sm text-red-600 bg-red-50 p-2 rounded">{error}</div>}
          {isEdit && (
            <div className="text-xs text-muted-foreground bg-muted/50 rounded px-3 py-1.5 font-mono">{form.staff_id}</div>
          )}
          <div>
            <Label className="text-xs">Name *</Label>
            <Input value={form.name} onChange={e => upd('name', e.target.value)} className="mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Geography</Label>
              <Select value={form.geography} onValueChange={v => upd('geography', v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">-- None --</SelectItem>
                  {GEOGRAPHIES.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Division</Label>
              <Select value={form.division} onValueChange={v => upd('division', v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">-- None --</SelectItem>
                  {DIVISIONS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Position Title</Label>
              <Input value={form.position} onChange={e => upd('position', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Grade</Label>
              <Input value={form.grade} onChange={e => upd('grade', e.target.value)} className="mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Alloc WWG</Label>
              <Input type="number" step="0.01" min="0" max="1" value={form.alloc_wwg} onChange={e => upd('alloc_wwg', e.target.value)} placeholder="0.0" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Alloc Tria</Label>
              <Input type="number" step="0.01" min="0" max="1" value={form.alloc_tria} onChange={e => upd('alloc_tria', e.target.value)} placeholder="0.0" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Alloc TFF</Label>
              <Input type="number" step="0.01" min="0" max="1" value={form.alloc_tff} onChange={e => upd('alloc_tff', e.target.value)} placeholder="0.0" className="mt-1" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ==================== CONTRACT DIALOG ==================== */

function ContractDialog({ open, onOpenChange, contract, staffId, onSave }: {
  open: boolean; onOpenChange: (v: boolean) => void;
  contract: Contract | null; staffId: string;
  onSave: (data: ContractFormData, isEdit: boolean) => Promise<void>;
}) {
  const isEdit = !!contract;
  const [form, setForm] = useState<ContractFormData>(emptyContractForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) {
      if (contract) {
        setForm({
          contract_id: contract.contract_id,
          staff_id: contract.staff_id || staffId,
          name: contract.name || '',
          legal_entity: contract.legal_entity || '__none__',
          position: contract.position || '',
          division: contract.division || '__none__',
          team: contract.team || '',
          agreement_type: contract.agreement_type || '__none__',
          opex_capex: contract.opex_capex || '__none__',
          currency: contract.currency || 'EUR',
          bank_net: contract.bank_net?.toString() || '',
          tax: contract.tax?.toString() || '',
          crypto_cash: contract.crypto_cash?.toString() || '',
          total_net: contract.total_net?.toString() || '',
          total_gross: contract.total_gross?.toString() || '',
          start_date: contract.start_date?.slice(0, 10) || '',
          end_date: contract.end_date?.slice(0, 10) || '',
          document_url: contract.document_url || '',
        });
      } else {
        setForm({ ...emptyContractForm, staff_id: staffId });
      }
      setError('');
    }
  }, [open, contract, staffId]);

  const handleSave = async () => {
    if (!form.contract_id.trim()) { setError('Contract ID is required'); return; }
    setSaving(true);
    try {
      await onSave(form, isEdit);
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const resp = await fetch(`/api/fin/upload-url?filename=contract-${form.contract_id}-${file.name}`, {
        method: 'PUT',
        body: file,
      });
      const data = await resp.json();
      if (data.url) {
        setForm(prev => ({ ...prev, document_url: data.url }));
      } else if (data.fallback) {
        setError('Blob storage not configured — file not uploaded');
      }
    } catch {
      setError('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const upd = (field: keyof ContractFormData, value: string) => setForm(prev => ({ ...prev, [field]: value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Contract' : 'Add Contract'}</DialogTitle>
          <DialogDescription>{isEdit ? `Update contract ${contract?.contract_id}` : `New contract for ${staffId}`}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {error && <div className="text-sm text-red-600 bg-red-50 p-2 rounded">{error}</div>}
          {isEdit && (
            <div className="text-xs text-muted-foreground bg-muted/50 rounded px-3 py-1.5 font-mono">
              {form.contract_id} &middot; {form.staff_id}
            </div>
          )}
          {!isEdit && form.staff_id && (
            <div className="text-xs text-muted-foreground bg-muted/50 rounded px-3 py-1.5">
              Employee: <span className="font-mono">{form.staff_id}</span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Contract Name</Label>
              <Input value={form.name} onChange={e => upd('name', e.target.value)} placeholder="e.g. Main employment" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Legal Entity</Label>
              <Select value={form.legal_entity} onValueChange={v => upd('legal_entity', v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">-- None --</SelectItem>
                  {ENTITIES.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Position</Label>
              <Input value={form.position} onChange={e => upd('position', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Agreement Type</Label>
              <Select value={form.agreement_type} onValueChange={v => upd('agreement_type', v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">-- None --</SelectItem>
                  {AGREEMENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Division</Label>
              <Select value={form.division} onValueChange={v => upd('division', v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">-- None --</SelectItem>
                  {DIVISIONS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Team</Label>
              <Input value={form.team} onChange={e => upd('team', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">OPEX / CAPEX</Label>
              <Select value={form.opex_capex} onValueChange={v => upd('opex_capex', v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">-- None --</SelectItem>
                  {OPEX_CAPEX.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Currency</Label>
              <Select value={form.currency} onValueChange={v => upd('currency', v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Start Date</Label>
              <Input type="date" value={form.start_date} onChange={e => upd('start_date', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">End Date</Label>
              <Input type="date" value={form.end_date} onChange={e => upd('end_date', e.target.value)} className="mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Bank Net</Label>
              <Input type="number" step="0.01" value={form.bank_net} onChange={e => upd('bank_net', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Tax</Label>
              <Input type="number" step="0.01" value={form.tax} onChange={e => upd('tax', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Crypto/Cash</Label>
              <Input type="number" step="0.01" value={form.crypto_cash} onChange={e => upd('crypto_cash', e.target.value)} className="mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Total Net</Label>
              <Input type="number" step="0.01" value={form.total_net} onChange={e => upd('total_net', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Total Gross</Label>
              <Input type="number" step="0.01" value={form.total_gross} onChange={e => upd('total_gross', e.target.value)} className="mt-1" />
            </div>
          </div>

          {/* Document upload */}
          <div>
            <Label className="text-xs">Contract Document</Label>
            <div className="mt-1 flex items-center gap-2">
              {form.document_url ? (
                <>
                  <a href={form.document_url} target="_blank" rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                    <FileText className="w-3 h-3" /> View document <ExternalLink className="w-3 h-3" />
                  </a>
                  <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => upd('document_url', '')}>
                    Remove
                  </Button>
                </>
              ) : (
                <label className="cursor-pointer">
                  <div className="flex items-center gap-1 text-xs text-muted-foreground border rounded px-3 py-1.5 hover:bg-muted/50">
                    <Upload className="w-3 h-3" /> {uploading ? 'Uploading...' : 'Upload PDF or image'}
                  </div>
                  <input type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden"
                    onChange={e => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = ''; }}
                    disabled={uploading} />
                </label>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving || uploading}>{saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ==================== CONFIRM DIALOG ==================== */

function ConfirmDialog({ open, onOpenChange, title, description, onConfirm, loading }: {
  open: boolean; onOpenChange: (v: boolean) => void;
  title: string; description: string;
  onConfirm: () => void; loading?: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="destructive" onClick={onConfirm} disabled={loading}>{loading ? 'Processing...' : 'Confirm'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ==================== TAB 1: EMPLOYEES ==================== */

function EmployeesTab({ employees, onRefresh, showStatus }: { employees: Employee[]; onRefresh: () => Promise<void>; showStatus: (msg: string, type: 'success' | 'error') => void }) {
  const fmt = usePayrollFmt();
  const [divisionFilter, setDivisionFilter] = useState('__all__');
  const [geoFilter, setGeoFilter] = useState('__all__');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deactivatingId, setDeactivatingId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  // Contract dialog state
  const [contractDialogOpen, setContractDialogOpen] = useState(false);
  const [editingContract, setEditingContract] = useState<Contract | null>(null);
  const [contractStaffId, setContractStaffId] = useState('');
  // Payroll DB snapshot, indexed by normalized employee name → latest month.
  // Loaded once on mount; ~1.7 MB file already cached by the workbench loader.
  const [payrollSnapshots, setPayrollSnapshots] = useState<Map<string, PayrollSnapshot>>(new Map());

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('fin_auth_token')
    fetch('/api/fin/dataset?source=payroll_actual', {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.json())
      .then((rows: PayrollSnapshot[]) => {
        if (cancelled) return;
        const byName = new Map<string, PayrollSnapshot>();
        for (const row of rows) {
          const key = normName((row as any).employee_name);
          if (!key) continue;
          const cur = byName.get(key);
          if (!cur || cur.year_month < row.year_month) byName.set(key, row);
        }
        setPayrollSnapshots(byName);
      })
      .catch(() => { /* file may not exist in some environments — non-fatal */ });
    return () => { cancelled = true; };
  }, []);

  const toggleRow = (id: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSaveEmployee = async (data: EmployeeFormData, isEdit: boolean) => {
    const body: Record<string, any> = {
      name: data.name,
      geography: data.geography === '__none__' ? null : data.geography,
      division: data.division === '__none__' ? null : data.division,
      position: data.position || null,
      grade: data.grade || null,
      alloc_wwg: data.alloc_wwg ? Number(data.alloc_wwg) : null,
      alloc_tria: data.alloc_tria ? Number(data.alloc_tria) : null,
      alloc_tff: data.alloc_tff ? Number(data.alloc_tff) : null,
    };
    if (isEdit) body.staff_id = data.staff_id;
    const resp = await fetch('/api/fin/employees', {
      method: isEdit ? 'PATCH' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(err.error || 'Failed to save');
    }
    showStatus(isEdit ? 'Employee updated' : 'Employee created', 'success');
    await onRefresh();
  };

  const handleDeactivate = async () => {
    if (!deactivatingId) return;
    setActionLoading(true);
    try {
      const resp = await fetch(`/api/fin/employees?staff_id=${encodeURIComponent(deactivatingId)}`, { method: 'DELETE' });
      if (!resp.ok) throw new Error('Deactivation failed');
      showStatus('Employee deactivated', 'success');
      setConfirmOpen(false);
      setDeactivatingId(null);
      await onRefresh();
    } catch {
      showStatus('Failed to deactivate employee', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveContract = async (data: ContractFormData, isEdit: boolean) => {
    const body: Record<string, any> = {
      contract_id: data.contract_id,
      staff_id: data.staff_id,
      name: data.name || null,
      legal_entity: data.legal_entity === '__none__' ? null : data.legal_entity,
      position: data.position || null,
      division: data.division === '__none__' ? null : data.division,
      team: data.team || null,
      agreement_type: data.agreement_type === '__none__' ? null : data.agreement_type,
      opex_capex: data.opex_capex === '__none__' ? null : data.opex_capex,
      currency: data.currency || 'EUR',
      bank_net: data.bank_net ? Number(data.bank_net) : null,
      tax: data.tax ? Number(data.tax) : null,
      crypto_cash: data.crypto_cash ? Number(data.crypto_cash) : null,
      total_net: data.total_net ? Number(data.total_net) : null,
      total_gross: data.total_gross ? Number(data.total_gross) : null,
      start_date: data.start_date || null,
      end_date: data.end_date || null,
      document_url: data.document_url || null,
    };
    const resp = await fetch('/api/fin/contracts', {
      method: isEdit ? 'PATCH' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(err.error || 'Failed to save contract');
    }
    showStatus(isEdit ? 'Contract updated' : 'Contract created', 'success');
    await onRefresh();
  };

  const handleDeactivateContract = async (contractId: string) => {
    if (!confirm('Deactivate this contract?')) return;
    try {
      const resp = await fetch(`/api/fin/contracts?contract_id=${encodeURIComponent(contractId)}`, { method: 'DELETE' });
      if (!resp.ok) throw new Error('Deactivation failed');
      showStatus('Contract deactivated', 'success');
      await onRefresh();
    } catch {
      showStatus('Failed to deactivate contract', 'error');
    }
  };

  const { filtered, divisions, geographies, summaryCards, allocationData, geoData } = useMemo(() => {
    const allDivisions = [...new Set(employees.map(e => e.division).filter(Boolean))] as string[];
    const allGeos = [...new Set(employees.map(e => e.geography).filter(Boolean))] as string[];

    let items = employees.filter(e => e.is_active);
    if (divisionFilter !== '__all__') items = items.filter(e => e.division === divisionFilter);
    if (geoFilter !== '__all__') items = items.filter(e => e.geography === geoFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter(e =>
        e.name.toLowerCase().includes(q) ||
        e.staff_id.toLowerCase().includes(q) ||
        (e.position && e.position.toLowerCase().includes(q))
      );
    }

    const headcount = items.length;
    // Total cost = employer_cost_eur (gross + employer SIC/NHS/COH/RED/IND)
    // when known, falling back to gross alone for non-Cyprus staff. The KPI
    // shows the headline true cost to the company that 1C books to PAYROLL EXPENSES.
    const totalGross = items.reduce((s, e) => s + (e.total_salary_gross || 0), 0);
    const totalCost = items.reduce(
      (s, e) => s + (e.employer_cost_eur ?? e.total_salary_gross ?? 0),
      0,
    );
    const totalEmployerContrib = totalCost - totalGross;
    const activeContracts = items.reduce((s, e) => s + (e.active_contracts || 0), 0);

    const byGeo = new Map<string, number>();
    for (const emp of items) {
      const geo = emp.geography || 'Unknown';
      byGeo.set(geo, (byGeo.get(geo) || 0) + 1);
    }

    const allocMap = new Map<string, number>();
    for (const emp of items) {
      // Allocate the FULL employer cost across groups (gross + contributions),
      // so the donut reflects "what each group really pays for headcount".
      const cost = emp.employer_cost_eur ?? emp.total_salary_gross ?? 0;
      if (emp.alloc_wwg && emp.alloc_wwg > 0)
        allocMap.set('WWG', (allocMap.get('WWG') || 0) + cost * emp.alloc_wwg);
      if (emp.alloc_tria && emp.alloc_tria > 0)
        allocMap.set('Tria', (allocMap.get('Tria') || 0) + cost * emp.alloc_tria);
      if (emp.alloc_tff && emp.alloc_tff > 0)
        allocMap.set('TFF', (allocMap.get('TFF') || 0) + cost * emp.alloc_tff);
      if (!emp.alloc_wwg && !emp.alloc_tria && !emp.alloc_tff)
        allocMap.set('Unallocated', (allocMap.get('Unallocated') || 0) + cost);
    }
    const allocation = Array.from(allocMap.entries())
      .map(([name, value]) => ({ name, value: Math.round(value) }))
      .sort((a, b) => b.value - a.value);

    const geoEntries = Array.from(byGeo.entries()).map(([name, value]) => ({ name, value }));

    const cards = [
      { label: 'Headcount', value: String(headcount), sub: `${activeContracts} active contracts`, color: 'text-blue-600' },
      { label: 'Total Gross Payroll', value: fmt(totalGross), sub: 'monthly · employee gross', color: 'text-emerald-600' },
      { label: 'Total Employer Cost', value: fmt(totalCost), sub: totalEmployerContrib > 0 ? `+ €${Math.round(totalEmployerContrib).toLocaleString()} SIC/NHS/COH/RED/IND` : 'monthly', color: 'text-indigo-600' },
      { label: 'Geographies', value: String(byGeo.size), sub: `${[...byGeo.keys()].slice(0, 3).join(', ')}${byGeo.size > 3 ? '…' : ''}`, color: 'text-amber-600' },
    ];

    return { filtered: items, divisions: allDivisions, geographies: allGeos, summaryCards: cards, allocationData: allocation, geoData: geoEntries };
  }, [employees, divisionFilter, geoFilter, searchQuery]);

  return (
    <div className="space-y-4 mt-4">
      <EmployeeDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        employee={editingEmployee}
        onSave={handleSaveEmployee}
      />
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Deactivate Employee"
        description="Are you sure you want to deactivate this employee? They will be marked as inactive."
        onConfirm={handleDeactivate}
        loading={actionLoading}
      />
      <ContractDialog
        open={contractDialogOpen}
        onOpenChange={setContractDialogOpen}
        contract={editingContract}
        staffId={contractStaffId}
        onSave={handleSaveContract}
      />

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {summaryCards.map(card => (
          <Card key={card.label} className="shadow-xs">
            <CardContent className="p-5">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">{card.label}</div>
              <div className={`text-2xl font-bold ${card.color}`}>{card.value}</div>
              {card.sub && <div className="text-[11px] text-muted-foreground mt-1">{card.sub}</div>}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {allocationData.length > 0 && (
          <Card className="shadow-xs">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Payroll Allocation by Group</CardTitle></CardHeader>
            <CardContent>
              <div className="flex items-center gap-6">
                <div className="w-44 h-44 flex-shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={allocationData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={3} dataKey="value" stroke="none">
                        {allocationData.map((_, index) => (
                          <Cell key={index} fill={DONUT_COLORS[index % DONUT_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v: number) => fmt(v)} contentStyle={{ fontSize: '12px', borderRadius: '8px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 space-y-2">
                  {allocationData.map((item, idx) => (
                    <div key={item.name} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: DONUT_COLORS[idx % DONUT_COLORS.length] }} />
                        <span className="text-sm">{item.name}</span>
                      </div>
                      <span className="text-sm font-mono">{fmt(item.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        {geoData.length > 0 && (
          <Card className="shadow-xs">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Headcount by Geography</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {geoData.sort((a, b) => b.value - a.value).map((geo, idx) => (
                  <div key={geo.name} className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: DONUT_COLORS[idx % DONUT_COLORS.length] }} />
                    <span className="text-sm flex-1">{geo.name}</span>
                    <Badge variant="secondary" className="text-xs font-mono">{geo.value}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Filters + Add button */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <Input placeholder="Search by name or ID..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="text-sm" />
            <Select value={divisionFilter} onValueChange={setDivisionFilter}>
              <SelectTrigger><SelectValue placeholder="Division" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Divisions</SelectItem>
                {divisions.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={geoFilter} onValueChange={setGeoFilter}>
              <SelectTrigger><SelectValue placeholder="Geography" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Geographies</SelectItem>
                {geographies.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={() => { setEditingEmployee(null); setDialogOpen(true); }} className="gap-1.5">
              <Plus size={14} /> Add Employee
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Employee table */}
      <Card>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-[10px] w-8" />
                <TableHead className="text-[10px]">Staff ID</TableHead>
                <TableHead className="text-[10px]">Name</TableHead>
                <TableHead className="text-[10px]">Geography</TableHead>
                <TableHead className="text-[10px]">Division</TableHead>
                <TableHead className="text-[10px] text-center">Contracts</TableHead>
                <TableHead className="text-[10px] text-right">Gross</TableHead>
                <TableHead className="text-[10px] text-right">Total Cost</TableHead>
                <TableHead className="text-[10px]">Allocation</TableHead>
                <TableHead className="text-[10px] w-20">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">No employees found</TableCell>
                </TableRow>
              )}
              {filtered.map(emp => (
                <EmployeeRow
                  key={emp.staff_id}
                  employee={emp}
                  payrollSnapshot={payrollSnapshots.get(normName(emp.name)) || null}
                  isExpanded={expandedRows.has(emp.staff_id)}
                  onToggle={() => toggleRow(emp.staff_id)}
                  onEdit={() => { setEditingEmployee(emp); setDialogOpen(true); }}
                  onDeactivate={() => { setDeactivatingId(emp.staff_id); setConfirmOpen(true); }}
                  onAddContract={() => { setEditingContract(null); setContractStaffId(emp.staff_id); setContractDialogOpen(true); }}
                  onEditContract={(c) => { setEditingContract(c); setContractStaffId(emp.staff_id); setContractDialogOpen(true); }}
                  onDeactivateContract={handleDeactivateContract}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}

function EmployeeRow({ employee, payrollSnapshot, isExpanded, onToggle, onEdit, onDeactivate, onAddContract, onEditContract, onDeactivateContract }: {
  employee: Employee;
  payrollSnapshot: PayrollSnapshot | null;
  isExpanded: boolean; onToggle: () => void;
  onEdit: () => void; onDeactivate: () => void;
  onAddContract: () => void; onEditContract: (c: Contract) => void; onDeactivateContract: (contractId: string) => void;
}) {
  const fmt = usePayrollFmt();
  const hasDetails =
    employee.contracts.length > 0 ||
    employee.positions.length > 0 ||
    payrollSnapshot != null;

  return (
    <>
      <TableRow className="cursor-pointer hover:bg-muted/50" onClick={onToggle}>
        <TableCell>
          {hasDetails ? (isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : null}
        </TableCell>
        <TableCell className="font-mono text-[10px] text-muted-foreground">{employee.staff_id}</TableCell>
        <TableCell className="text-xs font-medium">{employee.name}</TableCell>
        <TableCell className="text-xs">{employee.geography || '-'}</TableCell>
        <TableCell className="text-xs">{employee.division || '-'}</TableCell>
        <TableCell className="text-xs text-center">{employee.active_contracts || 0}</TableCell>
        <TableCell className="text-xs text-right font-mono">{fmt(employee.total_salary_gross)}</TableCell>
        <TableCell className="text-xs text-right font-mono">
          {employee.employer_cost_eur != null ? (
            <span title="Gross + employer SIC + NHS + COH + RED + IND (latest payroll month)">
              {fmt(employee.employer_cost_eur)}
            </span>
          ) : (
            <span className="text-muted-foreground/50">—</span>
          )}
        </TableCell>
        <TableCell>
          <AllocationBar wwg={employee.alloc_wwg} tria={employee.alloc_tria} tff={employee.alloc_tff} />
        </TableCell>
        <TableCell>
          <div className="flex gap-1" onClick={e => e.stopPropagation()}>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onEdit} title="Edit">
              <Pencil className="w-3 h-3" />
            </Button>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500 hover:text-red-700" onClick={onDeactivate} title="Deactivate">
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
      {isExpanded && hasDetails && (
        <TableRow>
          <TableCell colSpan={10} className="bg-muted/20 p-0">
            <div className="p-4 space-y-4">
              {/* Latest Cyprus payroll DB snapshot */}
              {payrollSnapshot && (
                <div>
                  <div className="text-xs font-semibold text-muted-foreground uppercase mb-2 flex items-center gap-2">
                    Latest Payroll Snapshot
                    <Badge variant="outline" className="text-[9px] font-normal">
                      {payrollSnapshot.company_name} · {payrollSnapshot.year_month}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="rounded border bg-background p-3">
                      <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Gross</div>
                      <div className="text-sm font-mono font-semibold mt-0.5">{fmt(payrollSnapshot.gross_eur)}</div>
                      <div className="text-[10px] text-muted-foreground mt-1">
                        Basic {fmt(payrollSnapshot.basic_eur)}
                        {payrollSnapshot.bonus_eur > 0 && ` · Bonus ${fmt(payrollSnapshot.bonus_eur)}`}
                      </div>
                    </div>
                    <div className="rounded border bg-background p-3">
                      <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Allowances + extras</div>
                      <div className="text-sm font-mono font-semibold mt-0.5">
                        {fmt(payrollSnapshot.allowances_eur + payrollSnapshot.extras_eur)}
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-1">
                        Allow {fmt(payrollSnapshot.allowances_eur)} · 13/14/15 {fmt(payrollSnapshot.extras_eur)}
                      </div>
                    </div>
                    <div className="rounded border bg-background p-3">
                      <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Employer Contributions</div>
                      <div className="text-sm font-mono font-semibold mt-0.5 text-indigo-700">
                        {fmt(payrollSnapshot.employer_total_eur)}
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-1 leading-tight">
                        SIC {fmt(payrollSnapshot.employer_sic_eur)} · NHS {fmt(payrollSnapshot.employer_nhs_eur)}<br />
                        COH {fmt(payrollSnapshot.employer_coh_eur)} · RED {fmt(payrollSnapshot.employer_red_eur)} · IND {fmt(payrollSnapshot.employer_ind_eur)}
                      </div>
                    </div>
                    <div className="rounded border bg-indigo-50 dark:bg-indigo-950/30 p-3">
                      <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Total Employer Cost</div>
                      <div className="text-sm font-mono font-semibold mt-0.5 text-indigo-700 dark:text-indigo-300">
                        {fmt(payrollSnapshot.total_cost_eur)}
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-1">
                        Insurable basis {fmt(payrollSnapshot.insurable_basis_eur)}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Contracts section */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold text-muted-foreground uppercase">Contracts ({employee.contracts.length})</div>
                  <Button variant="outline" size="sm" className="h-6 text-[10px] gap-1" onClick={onAddContract}>
                    <Plus className="w-3 h-3" /> Add Contract
                  </Button>
                </div>
                {employee.contracts.length > 0 && (
                  <div className="overflow-x-auto rounded border">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/30">
                          <TableHead className="text-[10px]">Contract ID</TableHead>
                          <TableHead className="text-[10px]">Legal Entity</TableHead>
                          <TableHead className="text-[10px]">Position</TableHead>
                          <TableHead className="text-[10px]">Type</TableHead>
                          <TableHead className="text-[10px]">Dates</TableHead>
                          <TableHead className="text-[10px]">Currency</TableHead>
                          <TableHead className="text-[10px] text-right">Bank Net</TableHead>
                          <TableHead className="text-[10px] text-right">Gross</TableHead>
                          <TableHead className="text-[10px]">Doc</TableHead>
                          <TableHead className="text-[10px]">Status</TableHead>
                          <TableHead className="text-[10px] w-16">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {employee.contracts.map(c => (
                          <TableRow key={c.contract_id} className="text-xs">
                            <TableCell className="font-mono text-[10px]">{c.contract_id}</TableCell>
                            <TableCell>{c.legal_entity || '-'}</TableCell>
                            <TableCell>{c.position || '-'}</TableCell>
                            <TableCell>{c.agreement_type || '-'}</TableCell>
                            <TableCell className="text-[10px] whitespace-nowrap">
                              {c.start_date ? fmtDate(c.start_date) : '-'}
                              {c.end_date ? ` → ${fmtDate(c.end_date)}` : ''}
                            </TableCell>
                            <TableCell>{c.currency || '-'}</TableCell>
                            <TableCell className="text-right font-mono">{fmt(c.bank_net, c.currency || 'EUR')}</TableCell>
                            <TableCell className="text-right font-mono">{fmt(c.total_gross, c.currency || 'EUR')}</TableCell>
                            <TableCell>
                              {c.document_url ? (
                                <a href={c.document_url} target="_blank" rel="noopener noreferrer" title="View document">
                                  <FileText className="w-3.5 h-3.5 text-blue-600 hover:text-blue-800" />
                                </a>
                              ) : (
                                <span className="text-muted-foreground/40">—</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge variant={c.is_active ? 'default' : 'secondary'} className={`text-[10px] ${c.is_active ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100' : ''}`}>
                                {c.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => onEditContract(c)} title="Edit contract">
                                  <Pencil className="w-3 h-3" />
                                </Button>
                                {c.is_active && (
                                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500 hover:text-red-700" onClick={() => onDeactivateContract(c.contract_id)} title="Deactivate contract">
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>

              {/* Positions section */}
              {employee.positions.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">Positions ({employee.positions.length})</div>
                  <div className="overflow-x-auto rounded border">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/30">
                          <TableHead className="text-[10px]">Position ID</TableHead>
                          <TableHead className="text-[10px]">Group</TableHead>
                          <TableHead className="text-[10px]">Department / Team</TableHead>
                          <TableHead className="text-[10px]">Position Name</TableHead>
                          <TableHead className="text-[10px] text-right">Allocation %</TableHead>
                          <TableHead className="text-[10px] text-right">Salary Gross</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {employee.positions.map(p => (
                          <TableRow key={p.id} className="text-xs">
                            <TableCell className="font-mono text-[10px]">{p.id}</TableCell>
                            <TableCell>{p.group_name}</TableCell>
                            <TableCell>{[p.department, p.team].filter(Boolean).join(' / ') || '-'}</TableCell>
                            <TableCell>{p.position_name}</TableCell>
                            <TableCell className="text-right font-mono">{p.allocation_pct != null ? `${(p.allocation_pct * 100).toFixed(0)}%` : '-'}</TableCell>
                            <TableCell className="text-right font-mono">{fmt(p.salary_gross)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

/* ==================== POSITION DIALOG ==================== */

interface PositionFormData {
  id: string;
  group_name: string;
  division: string;
  department: string;
  team: string;
  position_name: string;
  opex_capex: string;
  status: string;
  staff_id: string;
  allocation_pct: string;
  salary_gross: string;
}

const emptyPositionForm: PositionFormData = {
  id: '', group_name: '__none__', division: '', department: '', team: '',
  position_name: '', opex_capex: '__none__', status: 'Vacant', staff_id: '__none__',
  allocation_pct: '', salary_gross: '',
};

function PositionDialog({ open, onOpenChange, position, employees, onSave }: {
  open: boolean; onOpenChange: (v: boolean) => void;
  position: PositionRow | null; employees: Employee[];
  onSave: (data: PositionFormData, isEdit: boolean) => Promise<void>;
}) {
  const isEdit = !!position;
  const [form, setForm] = useState<PositionFormData>(emptyPositionForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      if (position) {
        setForm({
          id: position.id,
          group_name: position.group_name || '__none__',
          division: position.division || '',
          department: position.department || '',
          team: position.team || '',
          position_name: position.position_name,
          opex_capex: position.opex_capex || '__none__',
          status: position.status || 'Vacant',
          staff_id: position.staff_id || '__none__',
          allocation_pct: position.allocation_pct != null ? String(position.allocation_pct) : '',
          salary_gross: position.salary_gross != null ? String(position.salary_gross) : '',
        });
      } else {
        setForm({ ...emptyPositionForm, id: `POS-${Date.now().toString(36).toUpperCase()}` });
      }
      setError('');
    }
  }, [open, position]);

  const handleSave = async () => {
    if (!form.id.trim() || !form.position_name.trim()) {
      setError('ID and Position Name are required');
      return;
    }
    if (form.group_name === '__none__') {
      setError('Group is required');
      return;
    }
    setSaving(true);
    try {
      await onSave(form, isEdit);
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const upd = (field: keyof PositionFormData, value: string) => setForm(prev => ({ ...prev, [field]: value }));
  const activeEmployees = employees.filter(e => e.is_active);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Position' : 'Add Position'}</DialogTitle>
          <DialogDescription>{isEdit ? 'Update position details' : 'Create a new position'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {error && <div className="text-sm text-red-600 bg-red-50 p-2 rounded">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Position ID</Label>
              <Input value={form.id} onChange={e => upd('id', e.target.value)} disabled={isEdit} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Position Name *</Label>
              <Input value={form.position_name} onChange={e => upd('position_name', e.target.value)} className="mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Group *</Label>
              <Select value={form.group_name} onValueChange={v => upd('group_name', v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">-- Select --</SelectItem>
                  {GROUPS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Division</Label>
              <Input value={form.division} onChange={e => upd('division', e.target.value)} className="mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Department</Label>
              <Input value={form.department} onChange={e => upd('department', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Team</Label>
              <Input value={form.team} onChange={e => upd('team', e.target.value)} className="mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">OPEX / CAPEX</Label>
              <Select value={form.opex_capex} onValueChange={v => upd('opex_capex', v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">-- None --</SelectItem>
                  <SelectItem value="OPEX">OPEX</SelectItem>
                  <SelectItem value="CAPEX">CAPEX</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={v => upd('status', v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Filled">Filled</SelectItem>
                  <SelectItem value="Vacant">Vacant</SelectItem>
                  <SelectItem value="Closed">Closed</SelectItem>
                  <SelectItem value="Planned">Planned</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Assign Employee</Label>
            <Select value={form.staff_id} onValueChange={v => upd('staff_id', v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">-- Unassigned --</SelectItem>
                {activeEmployees.map(e => <SelectItem key={e.staff_id} value={e.staff_id}>{e.name} ({e.staff_id})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Allocation %</Label>
              <Input type="number" step="0.01" min="0" max="1" value={form.allocation_pct} onChange={e => upd('allocation_pct', e.target.value)} placeholder="0.0 - 1.0" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Salary Gross (EUR)</Label>
              <Input type="number" value={form.salary_gross} onChange={e => upd('salary_gross', e.target.value)} className="mt-1" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ==================== ASSIGN EMPLOYEE DIALOG ==================== */

function AssignEmployeeDialog({ open, onOpenChange, position, employees, onAssign }: {
  open: boolean; onOpenChange: (v: boolean) => void;
  position: PositionRow | null; employees: Employee[];
  onAssign: (positionId: string, staffId: string, employeeName: string) => Promise<void>;
}) {
  const [selectedStaffId, setSelectedStaffId] = useState('__none__');
  const [saving, setSaving] = useState(false);
  const activeEmployees = employees.filter(e => e.is_active);

  useEffect(() => {
    if (open) setSelectedStaffId('__none__');
  }, [open]);

  const handleAssign = async () => {
    if (!position || selectedStaffId === '__none__') return;
    const emp = activeEmployees.find(e => e.staff_id === selectedStaffId);
    if (!emp) return;
    setSaving(true);
    try {
      await onAssign(position.id, selectedStaffId, emp.name);
      onOpenChange(false);
    } catch {
      // error handled upstream
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Assign Employee</DialogTitle>
          <DialogDescription>Assign an employee to: {position?.position_name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Select value={selectedStaffId} onValueChange={setSelectedStaffId}>
            <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">-- Select --</SelectItem>
              {activeEmployees.map(e => <SelectItem key={e.staff_id} value={e.staff_id}>{e.name} ({e.staff_id})</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleAssign} disabled={saving || selectedStaffId === '__none__'}>{saving ? 'Assigning...' : 'Assign'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ==================== TAB 2: CONTRACTS REGISTRY ==================== */

function ContractsTab({ employees, onRefresh, showStatus }: {
  employees: Employee[]; onRefresh: () => Promise<void>;
  showStatus: (msg: string, type: 'success' | 'error') => void;
}) {
  const fmt = usePayrollFmt();
  const [contracts, setContracts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [entityFilter, setEntityFilter] = useState('__all__');
  const [typeFilter, setTypeFilter] = useState('__all__');
  const [statusFilter, setStatusFilter] = useState('active');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContract, setEditingContract] = useState<Contract | null>(null);
  const [contractStaffId, setContractStaffId] = useState('');

  const fetchContracts = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('fin_auth_token');
      const resp = await fetch('/api/fin/contracts', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (resp.ok) setContracts(await resp.json());
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchContracts(); }, [fetchContracts]);

  const handleRefresh = async () => { await fetchContracts(); await onRefresh(); };

  const handleSaveContract = async (data: ContractFormData, isEdit: boolean) => {
    const body: Record<string, any> = {
      staff_id: data.staff_id,
      name: data.name || null,
      legal_entity: data.legal_entity === '__none__' ? null : data.legal_entity,
      position: data.position || null,
      division: data.division === '__none__' ? null : data.division,
      team: data.team || null,
      agreement_type: data.agreement_type === '__none__' ? null : data.agreement_type,
      opex_capex: data.opex_capex === '__none__' ? null : data.opex_capex,
      currency: data.currency || 'EUR',
      bank_net: data.bank_net ? Number(data.bank_net) : null,
      tax: data.tax ? Number(data.tax) : null,
      crypto_cash: data.crypto_cash ? Number(data.crypto_cash) : null,
      total_net: data.total_net ? Number(data.total_net) : null,
      total_gross: data.total_gross ? Number(data.total_gross) : null,
      start_date: data.start_date || null,
      end_date: data.end_date || null,
      document_url: data.document_url || null,
    };
    if (isEdit) body.contract_id = data.contract_id;
    const resp = await fetch('/api/fin/contracts', {
      method: isEdit ? 'PATCH' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(err.error || 'Failed to save contract');
    }
    showStatus(isEdit ? 'Contract updated' : 'Contract created', 'success');
    await handleRefresh();
  };

  const handleDeactivate = async (contractId: string) => {
    if (!confirm('Deactivate this contract?')) return;
    try {
      const resp = await fetch(`/api/fin/contracts?contract_id=${encodeURIComponent(contractId)}`, { method: 'DELETE' });
      if (!resp.ok) throw new Error();
      showStatus('Contract deactivated', 'success');
      await handleRefresh();
    } catch { showStatus('Failed to deactivate', 'error'); }
  };

  // Build filter options from data
  const legalEntities = useMemo(() => [...new Set(contracts.map(c => c.legal_entity).filter(Boolean))].sort(), [contracts]);
  const agreementTypes = useMemo(() => [...new Set(contracts.map(c => c.agreement_type).filter(Boolean))].sort(), [contracts]);

  const filtered = useMemo(() => {
    return contracts.filter(c => {
      if (statusFilter === 'active' && !c.is_active) return false;
      if (statusFilter === 'inactive' && c.is_active) return false;
      if (entityFilter !== '__all__' && c.legal_entity !== entityFilter) return false;
      if (typeFilter !== '__all__' && c.agreement_type !== typeFilter) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const match = [c.contract_id, c.employee_name, c.position, c.legal_entity, c.name]
          .some(v => v && String(v).toLowerCase().includes(q));
        if (!match) return false;
      }
      return true;
    });
  }, [contracts, statusFilter, entityFilter, typeFilter, searchQuery]);

  const activeEmployees = useMemo(() => employees.filter(e => e.is_active), [employees]);

  if (loading) return <div className="py-8 text-center text-muted-foreground">Loading contracts...</div>;

  return (
    <div className="space-y-4 mt-4">
      <ContractDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        contract={editingContract}
        staffId={contractStaffId}
        onSave={handleSaveContract}
      />

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <Input placeholder="Search contracts..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-56 h-8 text-xs" />
        <Select value={entityFilter} onValueChange={setEntityFilter}>
          <SelectTrigger className="w-40 h-8 text-xs"><SelectValue placeholder="Entity" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Entities</SelectItem>
            {legalEntities.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-44 h-8 text-xs"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Types</SelectItem>
            {agreementTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-28 h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto">
          <Button size="sm" className="h-8 text-xs gap-1" onClick={() => {
            setEditingContract(null);
            setContractStaffId('');
            setDialogOpen(true);
          }}>
            <Plus className="w-3 h-3" /> Add Contract
          </Button>
        </div>
      </div>

      <div className="text-xs text-muted-foreground">{filtered.length} contract{filtered.length !== 1 ? 's' : ''}</div>

      {/* Table */}
      <div className="overflow-x-auto rounded border">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30">
              <TableHead className="text-[10px]">ID</TableHead>
              <TableHead className="text-[10px]">Employee</TableHead>
              <TableHead className="text-[10px]">Legal Entity</TableHead>
              <TableHead className="text-[10px]">Position</TableHead>
              <TableHead className="text-[10px]">Type</TableHead>
              <TableHead className="text-[10px]">Dates</TableHead>
              <TableHead className="text-[10px]">Currency</TableHead>
              <TableHead className="text-[10px] text-right">Bank Net</TableHead>
              <TableHead className="text-[10px] text-right">Tax</TableHead>
              <TableHead className="text-[10px] text-right">Gross</TableHead>
              <TableHead className="text-[10px]">Doc</TableHead>
              <TableHead className="text-[10px]">Status</TableHead>
              <TableHead className="text-[10px] w-16">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={13} className="text-center py-8 text-muted-foreground text-xs">No contracts found</TableCell></TableRow>
            )}
            {filtered.map(c => (
              <TableRow key={c.contract_id} className="text-xs">
                <TableCell className="font-mono text-[10px] text-muted-foreground">{c.contract_id}</TableCell>
                <TableCell className="text-xs font-medium">{c.employee_name || c.staff_id || '-'}</TableCell>
                <TableCell className="text-xs">{c.legal_entity || '-'}</TableCell>
                <TableCell className="text-xs">{c.position || '-'}</TableCell>
                <TableCell className="text-xs">{c.agreement_type || '-'}</TableCell>
                <TableCell className="text-[10px] whitespace-nowrap">
                  {c.start_date ? fmtDate(c.start_date) : '-'}
                  {c.end_date ? ` → ${fmtDate(c.end_date)}` : ''}
                </TableCell>
                <TableCell className="text-xs">{c.currency || '-'}</TableCell>
                <TableCell className="text-right font-mono text-xs">{fmt(c.bank_net, c.currency || 'EUR')}</TableCell>
                <TableCell className="text-right font-mono text-xs">{fmt(c.tax, c.currency || 'EUR')}</TableCell>
                <TableCell className="text-right font-mono text-xs">{fmt(c.total_gross, c.currency || 'EUR')}</TableCell>
                <TableCell>
                  {c.document_url ? (
                    <a href={c.document_url} target="_blank" rel="noopener noreferrer" title="View document">
                      <FileText className="w-3.5 h-3.5 text-blue-600 hover:text-blue-800" />
                    </a>
                  ) : <span className="text-muted-foreground/40">—</span>}
                </TableCell>
                <TableCell>
                  <Badge variant={c.is_active ? 'default' : 'secondary'} className={`text-[10px] ${c.is_active ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100' : ''}`}>
                    {c.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => {
                      setEditingContract(c);
                      setContractStaffId(c.staff_id || '');
                      setDialogOpen(true);
                    }} title="Edit">
                      <Pencil className="w-3 h-3" />
                    </Button>
                    {c.is_active && (
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500 hover:text-red-700" onClick={() => handleDeactivate(c.contract_id)} title="Deactivate">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

/* ==================== TAB 3: POSITIONS ==================== */

function PositionsTab({ positions, loading, orgData, employees, onRefresh, showStatus }: {
  positions: PositionRow[]; loading: boolean; orgData: OrgData | null;
  employees: Employee[]; onRefresh: () => Promise<void>;
  showStatus: (msg: string, type: 'success' | 'error') => void;
}) {
  const fmt = usePayrollFmt();
  const [groupFilter, setGroupFilter] = useState('__all__');
  const [divisionFilter, setDivisionFilter] = useState('__all__');
  const [statusFilter, setStatusFilter] = useState('__all__');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPosition, setEditingPosition] = useState<PositionRow | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assigningPosition, setAssigningPosition] = useState<PositionRow | null>(null);

  const toggleGroup = (key: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const handleSavePosition = async (data: PositionFormData, isEdit: boolean) => {
    const body: Record<string, any> = {
      id: data.id,
      group_name: data.group_name === '__none__' ? null : data.group_name,
      division: data.division || null,
      department: data.department || null,
      team: data.team || null,
      position_name: data.position_name,
      opex_capex: data.opex_capex === '__none__' ? null : data.opex_capex,
      status: data.status,
      staff_id: data.staff_id === '__none__' ? null : data.staff_id,
      allocation_pct: data.allocation_pct ? Number(data.allocation_pct) : null,
      salary_gross: data.salary_gross ? Number(data.salary_gross) : null,
    };
    // If assigning an employee, also set employee_name and status to Filled
    if (body.staff_id) {
      const emp = employees.find(e => e.staff_id === body.staff_id);
      if (emp) body.employee_name = emp.name;
      if (body.status === 'Vacant') body.status = 'Filled';
    }
    const resp = await fetch('/api/fin/positions', {
      method: isEdit ? 'PATCH' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(err.error || 'Failed to save');
    }
    showStatus(isEdit ? 'Position updated' : 'Position created', 'success');
    await onRefresh();
  };

  const handleDeletePosition = async () => {
    if (!deletingId) return;
    setActionLoading(true);
    try {
      const resp = await fetch(`/api/fin/positions?id=${encodeURIComponent(deletingId)}`, { method: 'DELETE' });
      if (!resp.ok) throw new Error('Delete failed');
      showStatus('Position deleted', 'success');
      setConfirmOpen(false);
      setDeletingId(null);
      await onRefresh();
    } catch {
      showStatus('Failed to delete position', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssignEmployee = async (positionId: string, staffId: string, employeeName: string) => {
    const resp = await fetch('/api/fin/positions', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: positionId, staff_id: staffId, employee_name: employeeName, status: 'Filled' }),
    });
    if (!resp.ok) {
      showStatus('Failed to assign employee', 'error');
      throw new Error('Assign failed');
    }
    showStatus('Employee assigned to position', 'success');
    await onRefresh();
  };

  const { filtered, groups, divisions, summary } = useMemo(() => {
    let items = positions;
    if (groupFilter !== '__all__') items = items.filter(p => p.group_name === groupFilter);
    if (divisionFilter !== '__all__') items = items.filter(p => p.division === divisionFilter);
    if (statusFilter !== '__all__') items = items.filter(p => p.status === statusFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter(p =>
        p.position_name.toLowerCase().includes(q) ||
        (p.employee_name && p.employee_name.toLowerCase().includes(q)) ||
        p.id.toLowerCase().includes(q)
      );
    }

    const allGroups = [...new Set(positions.map(p => p.group_name))].sort();
    const allDivisions = [...new Set(positions.map(p => p.division).filter(Boolean))] as string[];

    return {
      filtered: items,
      groups: allGroups,
      divisions: allDivisions,
      summary: {
        total: items.length,
        filled: items.filter(p => p.status === 'Filled').length,
        vacant: items.filter(p => p.status === 'Vacant').length,
        closed: items.filter(p => p.status === 'Closed').length,
        planned: items.filter(p => p.status === 'Planned').length,
        totalSalary: items.reduce((s, p) => s + (p.salary_gross || 0), 0),
      },
    };
  }, [positions, groupFilter, divisionFilter, statusFilter, searchQuery]);

  // Group positions by group -> division -> department -> team
  const grouped = useMemo(() => {
    const map = new Map<string, Map<string, PositionRow[]>>();
    for (const p of filtered) {
      const gKey = p.group_name;
      if (!map.has(gKey)) map.set(gKey, new Map());
      const dKey = [p.division, p.department, p.team].filter(Boolean).join(' > ');
      if (!map.get(gKey)!.has(dKey)) map.get(gKey)!.set(dKey, []);
      map.get(gKey)!.get(dKey)!.push(p);
    }
    return map;
  }, [filtered]);

  if (loading) return <div className="space-y-4 mt-4"><Skeleton className="h-24" /><Skeleton className="h-64" /></div>;

  return (
    <div className="space-y-4 mt-4">
      <PositionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        position={editingPosition}
        employees={employees}
        onSave={handleSavePosition}
      />
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete Position"
        description="Are you sure you want to delete this position? This action cannot be undone."
        onConfirm={handleDeletePosition}
        loading={actionLoading}
      />
      <AssignEmployeeDialog
        open={assignDialogOpen}
        onOpenChange={setAssignDialogOpen}
        position={assigningPosition}
        employees={employees}
        onAssign={handleAssignEmployee}
      />

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
        <Card className="shadow-xs"><CardContent className="p-4">
          <div className="text-xs text-muted-foreground uppercase mb-1">Total</div>
          <div className="text-xl font-bold">{summary.total}</div>
        </CardContent></Card>
        <Card className="shadow-xs"><CardContent className="p-4">
          <div className="text-xs text-muted-foreground uppercase mb-1">Filled</div>
          <div className="text-xl font-bold text-emerald-600">{summary.filled}</div>
        </CardContent></Card>
        <Card className="shadow-xs"><CardContent className="p-4">
          <div className="text-xs text-muted-foreground uppercase mb-1">Vacant</div>
          <div className="text-xl font-bold text-amber-600">{summary.vacant}</div>
        </CardContent></Card>
        <Card className="shadow-xs"><CardContent className="p-4">
          <div className="text-xs text-muted-foreground uppercase mb-1">Closed</div>
          <div className="text-xl font-bold text-gray-500">{summary.closed}</div>
        </CardContent></Card>
        <Card className="shadow-xs"><CardContent className="p-4">
          <div className="text-xs text-muted-foreground uppercase mb-1">Planned</div>
          <div className="text-xl font-bold text-blue-600">{summary.planned}</div>
        </CardContent></Card>
        <Card className="shadow-xs"><CardContent className="p-4">
          <div className="text-xs text-muted-foreground uppercase mb-1">Total Salary</div>
          <div className="text-xl font-bold">{fmt(summary.totalSalary)}</div>
        </CardContent></Card>
      </div>

      {/* Filters + Add button */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
            <Input placeholder="Search positions..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="text-sm" />
            <Select value={groupFilter} onValueChange={setGroupFilter}>
              <SelectTrigger><SelectValue placeholder="Group" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Groups</SelectItem>
                {groups.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={divisionFilter} onValueChange={setDivisionFilter}>
              <SelectTrigger><SelectValue placeholder="Division" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Divisions</SelectItem>
                {divisions.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Statuses</SelectItem>
                <SelectItem value="Filled">Filled</SelectItem>
                <SelectItem value="Vacant">Vacant</SelectItem>
                <SelectItem value="Closed">Closed</SelectItem>
                <SelectItem value="Planned">Planned</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={() => { setEditingPosition(null); setDialogOpen(true); }} className="gap-1.5">
              <Plus size={14} /> Add Position
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Multi-layer positions table */}
      <div className="overflow-x-auto rounded border">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30">
              <TableHead className="text-[10px] w-8"></TableHead>
              <TableHead className="text-[10px]">ID</TableHead>
              <TableHead className="text-[10px]">Position Name</TableHead>
              <TableHead className="text-[10px]">Status</TableHead>
              <TableHead className="text-[10px]">Employee</TableHead>
              <TableHead className="text-[10px] text-right">Allocation</TableHead>
              <TableHead className="text-[10px] text-right">Salary Gross</TableHead>
              <TableHead className="text-[10px]">OPEX/CAPEX</TableHead>
              <TableHead className="text-[10px] w-20">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground text-xs">No positions found</TableCell></TableRow>
            )}
            {Array.from(grouped.entries()).map(([groupName, divMap]) => {
              const groupPositions = Array.from(divMap.values()).flat();
              const groupSalary = groupPositions.reduce((s, p) => s + (p.salary_gross || 0), 0);
              const groupFilled = groupPositions.filter(p => p.status === 'Filled').length;
              const isGroupOpen = expandedGroups.has(groupName);
              return (
                <React.Fragment key={groupName}>
                  <TableRow className="bg-muted/20 cursor-pointer hover:bg-muted/40" onClick={() => toggleGroup(groupName)}>
                    <TableCell className="py-2">
                      {isGroupOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    </TableCell>
                    <TableCell colSpan={4} className="py-2">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="text-xs font-semibold">{groupName}</span>
                        <Badge variant="secondary" className="text-[9px]">{groupPositions.length} positions</Badge>
                        <Badge variant="outline" className="text-[9px]">{groupFilled} filled</Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-right text-xs font-mono py-2"></TableCell>
                    <TableCell className="text-right text-xs font-mono py-2">{fmt(groupSalary)}</TableCell>
                    <TableCell colSpan={2} className="py-2"></TableCell>
                  </TableRow>
                  {isGroupOpen && Array.from(divMap.entries()).map(([divPath, posItems]) => {
                    const divKey = `${groupName}::${divPath}`;
                    const isDivOpen = expandedGroups.has(divKey);
                    const divSalary = posItems.reduce((s, p) => s + (p.salary_gross || 0), 0);
                    return (
                      <React.Fragment key={divKey}>
                        <TableRow className="bg-muted/10 cursor-pointer hover:bg-muted/30" onClick={() => toggleGroup(divKey)}>
                          <TableCell className="py-1.5"></TableCell>
                          <TableCell className="py-1.5">
                            {isDivOpen ? <ChevronDown className="w-3 h-3 text-muted-foreground" /> : <ChevronRight className="w-3 h-3 text-muted-foreground" />}
                          </TableCell>
                          <TableCell colSpan={3} className="py-1.5">
                            <span className="text-xs text-muted-foreground">{divPath || 'Unassigned'}</span>
                            <Badge variant="outline" className="text-[9px] ml-2">{posItems.length}</Badge>
                          </TableCell>
                          <TableCell className="text-right text-xs font-mono py-1.5"></TableCell>
                          <TableCell className="text-right text-xs font-mono py-1.5">{fmt(divSalary)}</TableCell>
                          <TableCell colSpan={2} className="py-1.5"></TableCell>
                        </TableRow>
                        {isDivOpen && posItems.map(p => (
                          <TableRow key={p.id} className={p.status === 'Vacant' ? 'bg-amber-50/30' : ''}>
                            <TableCell></TableCell>
                            <TableCell className="font-mono text-[10px] text-muted-foreground">{p.id}</TableCell>
                            <TableCell className="text-xs font-medium">{p.position_name}</TableCell>
                            <TableCell><StatusBadge status={p.status} /></TableCell>
                            <TableCell className="text-xs">
                              {p.employee_name || p.employee_full_name || (p.status === 'Vacant' ? <span className="text-amber-600 italic">Vacant</span> : '-')}
                            </TableCell>
                            <TableCell className="text-xs text-right font-mono">
                              {p.allocation_pct != null ? `${(p.allocation_pct * 100).toFixed(0)}%` : '-'}
                            </TableCell>
                            <TableCell className="text-xs text-right font-mono">{fmt(p.salary_gross)}</TableCell>
                            <TableCell className="text-xs">{p.opex_capex || '-'}</TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { setEditingPosition(p); setDialogOpen(true); }} title="Edit">
                                  <Pencil className="w-3 h-3" />
                                </Button>
                                {p.status === 'Vacant' && (
                                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-blue-600" onClick={() => { setAssigningPosition(p); setAssignDialogOpen(true); }} title="Assign">
                                    <UserCheck className="w-3 h-3" />
                                  </Button>
                                )}
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500 hover:text-red-700" onClick={() => { setDeletingId(p.id); setConfirmOpen(true); }} title="Delete">
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

/* ==================== HIRING PLAN DIALOG ==================== */

interface HiringFormData {
  id: string;
  group_name: string;
  division: string;
  department: string;
  team: string;
  vacancy_name: string;
  hiring_manager: string;
  legal_entity: string;
  geography: string;
  recruitment_type: string;
  salary_cap: string;
  salary_currency: string;
  hire_date_est: string;
  note: string;
}

const emptyHiringForm: HiringFormData = {
  id: '', group_name: '', division: '', department: '', team: '',
  vacancy_name: '', hiring_manager: '', legal_entity: '__none__',
  geography: '__none__', recruitment_type: '__none__', salary_cap: '',
  salary_currency: 'EUR', hire_date_est: '', note: '',
};

function HiringDialog({ open, onOpenChange, entry, onSave }: {
  open: boolean; onOpenChange: (v: boolean) => void;
  entry: HiringEntry | null;
  onSave: (data: HiringFormData, isEdit: boolean) => Promise<void>;
}) {
  const isEdit = !!entry;
  const [form, setForm] = useState<HiringFormData>(emptyHiringForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      if (entry) {
        setForm({
          id: entry.id,
          group_name: entry.group_name || '',
          division: entry.division || '',
          department: entry.department || '',
          team: entry.team || '',
          vacancy_name: entry.vacancy_name || '',
          hiring_manager: entry.hiring_manager || '',
          legal_entity: entry.legal_entity || '__none__',
          geography: entry.geography || '__none__',
          recruitment_type: entry.recruitment_type || '__none__',
          salary_cap: entry.salary_cap?.toString() || '',
          salary_currency: entry.salary_currency || 'EUR',
          hire_date_est: entry.hire_date_est ? entry.hire_date_est.split('T')[0] : '',
          note: entry.note || '',
        });
      } else {
        setForm({ ...emptyHiringForm, id: `HP-${Date.now().toString(36).toUpperCase()}` });
      }
      setError('');
    }
  }, [open, entry]);

  const handleSave = async () => {
    if (!form.id.trim() || !form.vacancy_name.trim()) {
      setError('ID and Vacancy Name are required');
      return;
    }
    setSaving(true);
    try {
      await onSave(form, isEdit);
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const upd = (field: keyof HiringFormData, value: string) => setForm(prev => ({ ...prev, [field]: value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Vacancy' : 'Add Vacancy'}</DialogTitle>
          <DialogDescription>{isEdit ? 'Update hiring plan entry' : 'Create a new vacancy'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {error && <div className="text-sm text-red-600 bg-red-50 p-2 rounded">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">ID</Label>
              <Input value={form.id} onChange={e => upd('id', e.target.value)} disabled={isEdit} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Vacancy Name *</Label>
              <Input value={form.vacancy_name} onChange={e => upd('vacancy_name', e.target.value)} className="mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Group</Label>
              <Input value={form.group_name} onChange={e => upd('group_name', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Division</Label>
              <Input value={form.division} onChange={e => upd('division', e.target.value)} className="mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Department</Label>
              <Input value={form.department} onChange={e => upd('department', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Team</Label>
              <Input value={form.team} onChange={e => upd('team', e.target.value)} className="mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Hiring Manager</Label>
              <Input value={form.hiring_manager} onChange={e => upd('hiring_manager', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Legal Entity</Label>
              <Select value={form.legal_entity} onValueChange={v => upd('legal_entity', v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">-- None --</SelectItem>
                  {ENTITIES.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Geography</Label>
              <Select value={form.geography} onValueChange={v => upd('geography', v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">-- None --</SelectItem>
                  {GEOGRAPHIES.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Recruitment Type</Label>
              <Select value={form.recruitment_type} onValueChange={v => upd('recruitment_type', v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">-- None --</SelectItem>
                  <SelectItem value="Internal">Internal</SelectItem>
                  <SelectItem value="External">External</SelectItem>
                  <SelectItem value="Replacement">Replacement</SelectItem>
                  <SelectItem value="New">New</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Salary Cap</Label>
              <Input type="number" value={form.salary_cap} onChange={e => upd('salary_cap', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Currency</Label>
              <Select value={form.salary_currency} onValueChange={v => upd('salary_currency', v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="CHF">CHF</SelectItem>
                  <SelectItem value="AED">AED</SelectItem>
                  <SelectItem value="GBP">GBP</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Est. Hire Date</Label>
              <Input type="date" value={form.hire_date_est} onChange={e => upd('hire_date_est', e.target.value)} className="mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Note</Label>
            <Input value={form.note} onChange={e => upd('note', e.target.value)} className="mt-1" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ==================== TAB 3: HIRING PLAN ==================== */

function HiringTab({ hiringData, loading, onRefresh, showStatus }: {
  hiringData: HiringEntry[]; loading: boolean;
  onRefresh: () => Promise<void>; showStatus: (msg: string, type: 'success' | 'error') => void;
}) {
  const fmt = usePayrollFmt();
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<HiringEntry | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const handleSaveHiring = async (data: HiringFormData, isEdit: boolean) => {
    const body: Record<string, any> = {
      id: data.id,
      group_name: data.group_name || null,
      division: data.division || null,
      department: data.department || null,
      team: data.team || null,
      vacancy_name: data.vacancy_name,
      hiring_manager: data.hiring_manager || null,
      legal_entity: data.legal_entity === '__none__' ? null : data.legal_entity,
      geography: data.geography === '__none__' ? null : data.geography,
      recruitment_type: data.recruitment_type === '__none__' ? null : data.recruitment_type,
      salary_cap: data.salary_cap ? Number(data.salary_cap) : null,
      salary_currency: data.salary_currency,
      hire_date_est: data.hire_date_est || null,
      note: data.note || null,
    };
    const resp = await fetch('/api/fin/hiring-plan', {
      method: isEdit ? 'PATCH' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(err.error || 'Failed to save');
    }
    showStatus(isEdit ? 'Vacancy updated' : 'Vacancy created', 'success');
    await onRefresh();
  };

  const handleDeleteHiring = async () => {
    if (!deletingId) return;
    setActionLoading(true);
    try {
      const resp = await fetch(`/api/fin/hiring-plan?id=${encodeURIComponent(deletingId)}`, { method: 'DELETE' });
      if (!resp.ok) throw new Error('Delete failed');
      showStatus('Vacancy deleted', 'success');
      setConfirmOpen(false);
      setDeletingId(null);
      await onRefresh();
    } catch {
      showStatus('Failed to delete vacancy', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const { filtered, summary } = useMemo(() => {
    let items = hiringData;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter(h =>
        (h.vacancy_name && h.vacancy_name.toLowerCase().includes(q)) ||
        (h.department && h.department.toLowerCase().includes(q)) ||
        (h.team && h.team.toLowerCase().includes(q))
      );
    }
    return {
      filtered: items,
      summary: {
        totalPlanned: items.length,
        totalMonthlyCost: items.reduce((s, h) => s + (h.salary_cap || 0), 0),
      },
    };
  }, [hiringData, searchQuery]);

  if (loading) return <div className="space-y-4 mt-4"><Skeleton className="h-24" /><Skeleton className="h-64" /></div>;

  return (
    <div className="space-y-4 mt-4">
      <HiringDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        entry={editingEntry}
        onSave={handleSaveHiring}
      />
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete Vacancy"
        description="Are you sure you want to delete this vacancy? This action cannot be undone."
        onConfirm={handleDeleteHiring}
        loading={actionLoading}
      />

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="shadow-xs"><CardContent className="p-5">
          <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Planned Hires</div>
          <div className="text-2xl font-bold text-blue-600">{summary.totalPlanned}</div>
        </CardContent></Card>
        <Card className="shadow-xs"><CardContent className="p-5">
          <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Total Monthly Cost Impact</div>
          <div className="text-2xl font-bold text-emerald-600">{fmt(summary.totalMonthlyCost)}</div>
        </CardContent></Card>
        <Card className="shadow-xs"><CardContent className="p-5">
          <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Projected Annual Impact</div>
          <div className="text-2xl font-bold text-amber-600">{fmt(summary.totalMonthlyCost * 12)}</div>
        </CardContent></Card>
      </div>

      {/* Search + Add button */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-3">
            <Input placeholder="Search vacancies..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="text-sm flex-1 max-w-sm" />
            <Button onClick={() => { setEditingEntry(null); setDialogOpen(true); }} className="gap-1.5">
              <Plus size={14} /> Add Vacancy
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Hiring table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <UserPlus size={14} /> Upcoming Hires
          </CardTitle>
        </CardHeader>
        <div className="overflow-x-auto">
          {filtered.length === 0 ? (
            <CardContent className="py-8 text-center text-muted-foreground">No hiring plan entries found</CardContent>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-[10px]">Vacancy</TableHead>
                  <TableHead className="text-[10px]">Division / Team</TableHead>
                  <TableHead className="text-[10px]">Legal Entity</TableHead>
                  <TableHead className="text-[10px]">Geography</TableHead>
                  <TableHead className="text-[10px]">Hiring Manager</TableHead>
                  <TableHead className="text-[10px] text-right">Salary Cap</TableHead>
                  <TableHead className="text-[10px]">Est. Hire Date</TableHead>
                  <TableHead className="text-[10px]">Type</TableHead>
                  <TableHead className="text-[10px]">Note</TableHead>
                  <TableHead className="text-[10px] w-20">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(h => (
                  <TableRow key={h.id}>
                    <TableCell className="text-sm font-medium">{h.vacancy_name || '-'}</TableCell>
                    <TableCell className="text-sm">{[h.division, h.department, h.team].filter(Boolean).join(' / ') || '-'}</TableCell>
                    <TableCell className="text-sm">{h.legal_entity || '-'}</TableCell>
                    <TableCell className="text-sm">{h.geography || '-'}</TableCell>
                    <TableCell className="text-sm">{h.hiring_manager || '-'}</TableCell>
                    <TableCell className="text-sm text-right font-mono">
                      {h.salary_cap != null ? fmt(h.salary_cap, h.salary_currency || 'EUR') : '-'}
                    </TableCell>
                    <TableCell className="text-sm">{fmtDate(h.hire_date_est)}</TableCell>
                    <TableCell className="text-sm">
                      {h.recruitment_type ? <Badge variant="secondary" className="text-[10px]">{h.recruitment_type}</Badge> : '-'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">{h.note || '-'}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditingEntry(h); setDialogOpen(true); }} title="Edit">
                          <Pencil size={12} />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={() => { setDeletingId(h.id); setConfirmOpen(true); }} title="Delete">
                          <Trash2 size={12} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </Card>
    </div>
  );
}

/* ==================== TAB 4: ORG STRUCTURE ==================== */

function OrgTab({ orgData, loading }: { orgData: OrgData | null; loading: boolean }) {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  const toggleNode = (key: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  if (loading || !orgData) {
    return <div className="space-y-4 mt-4"><Skeleton className="h-24" /><Skeleton className="h-64" /></div>;
  }

  const { hierarchy } = orgData;

  return (
    <div className="space-y-4 mt-4">
      <Card className="shadow-xs">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Network size={14} /> Organization Hierarchy
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {Object.entries(hierarchy).sort(([a], [b]) => a.localeCompare(b)).map(([groupName, groupData]) => {
              const gKey = groupName;
              const isGroupOpen = expandedNodes.has(gKey);

              return (
                <div key={gKey} className="border rounded-lg">
                  <button
                    className="w-full flex items-center gap-2 p-3 hover:bg-muted/50 transition-colors text-left"
                    onClick={() => toggleNode(gKey)}
                  >
                    {isGroupOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <Building2 size={14} className="text-primary" />
                    <span className="font-medium text-sm">{groupName}</span>
                    <div className="flex gap-2 ml-auto">
                      <Badge variant="secondary" className="text-[10px]">{groupData.total} positions</Badge>
                      <Badge className="text-[10px] bg-emerald-100 text-emerald-800 hover:bg-emerald-100">{groupData.filled} filled</Badge>
                      {groupData.vacant > 0 && <Badge className="text-[10px] bg-amber-100 text-amber-800 hover:bg-amber-100">{groupData.vacant} vacant</Badge>}
                    </div>
                  </button>

                  {isGroupOpen && (
                    <div className="border-t px-3 pb-3">
                      {Object.entries(groupData.divisions).sort(([a], [b]) => a.localeCompare(b)).map(([divName, divData]) => {
                        const dKey = `${gKey}>${divName}`;
                        const isDivOpen = expandedNodes.has(dKey);

                        return (
                          <div key={dKey} className="ml-4 mt-2">
                            <button
                              className="w-full flex items-center gap-2 p-2 hover:bg-muted/30 rounded transition-colors text-left"
                              onClick={() => toggleNode(dKey)}
                            >
                              {isDivOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                              <span className="text-sm">{divName}</span>
                              <Badge variant="outline" className="text-[10px] ml-auto">{divData.total}</Badge>
                            </button>

                            {isDivOpen && (
                              <div className="ml-4">
                                {Object.entries(divData.departments).sort(([a], [b]) => a.localeCompare(b)).map(([deptName, deptData]) => {
                                  const depKey = `${dKey}>${deptName}`;
                                  const isDepOpen = expandedNodes.has(depKey);

                                  return (
                                    <div key={depKey} className="mt-1">
                                      <button
                                        className="w-full flex items-center gap-2 p-1.5 hover:bg-muted/20 rounded transition-colors text-left"
                                        onClick={() => toggleNode(depKey)}
                                      >
                                        {isDepOpen ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
                                        <span className="text-xs">{deptName}</span>
                                        <Badge variant="outline" className="text-[9px] ml-auto">{deptData.total}</Badge>
                                      </button>

                                      {isDepOpen && (
                                        <div className="ml-4 space-y-0.5">
                                          {Object.entries(deptData.teams).sort(([a], [b]) => a.localeCompare(b)).map(([teamName, teamData]) => (
                                            <div key={teamName} className="flex items-center gap-2 p-1 pl-2 text-xs text-muted-foreground">
                                              <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/30" />
                                              <span>{teamName}</span>
                                              <span className="ml-auto font-mono text-[10px]">
                                                {teamData.filled}F / {teamData.vacant}V / {teamData.total}T
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
